import React, { useState, useEffect, useCallback } from "react";
import { Box, CircularProgress, Alert, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceDot,
} from "recharts";
import axios from "axios";

const DECISION_COLORS = {
  BUY: "#4caf50",
  OUTPERFORM: "#81c784",
  SELL: "#f44336",
  UNDERPERFORM: "#ef9a9a",
  HOLD: "#ffeb3b",
};

const getDecisionColor = (decision) =>
  DECISION_COLORS[decision] || "#888888";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <Box
      sx={{
        backgroundColor: "rgba(26,26,26,0.95)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        minWidth: 140,
      }}
    >
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
        {data.date}
      </Typography>
      <Typography variant="body2" sx={{ color: "#00ff00", fontWeight: 600 }}>
        ${data.close?.toFixed(2)}
      </Typography>
      {data.analyses && data.analyses.length > 0 && (
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          {data.analyses.map((a, i) => (
            <Box key={i} sx={{ mb: i < data.analyses.length - 1 ? 0.5 : 0 }}>
              <Typography
                variant="body2"
                sx={{ color: getDecisionColor(a.decision), fontWeight: 700 }}
              >
                {a.decision}
              </Typography>
              {a.confidence != null && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  Confidence: {(a.confidence * 100).toFixed(0)}%
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const renderAnalysisDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload.analyses || payload.analyses.length === 0 || cx == null || cy == null) return null;
  return (
    <g>
      {payload.analyses.map((a, i) => {
        const color = getDecisionColor(a.decision);
        const dotY = cy + i * 4;
        return (
          <g key={i}>
            <circle cx={cx} cy={dotY} r={7} fill={color} stroke="#1a1a1a" strokeWidth={2} />
            <text
              x={cx}
              y={cy - 14 - i * 14}
              textAnchor="middle"
              fill={color}
              fontSize={10}
              fontWeight={700}
            >
              {a.decision}
            </text>
          </g>
        );
      })}
    </g>
  );
};

function StockPriceChart({ ticker }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/stock/${ticker}/price-chart`);
      if (response.data.success) {
        const { prices, analyses } = response.data;

        if (!prices || prices.length === 0) {
          setChartData([]);
          return;
        }

        const tradingDates = prices.map((p) => p.date).sort();

        const findNearestTradingDate = (dateStr) => {
          if (tradingDates.includes(dateStr)) return dateStr;
          for (let i = tradingDates.length - 1; i >= 0; i--) {
            if (tradingDates[i] <= dateStr) return tradingDates[i];
          }
          return tradingDates[0];
        };

        const analysisMap = {};
        (analyses || []).forEach((a) => {
          const mappedDate = findNearestTradingDate(a.date);
          if (!analysisMap[mappedDate]) {
            analysisMap[mappedDate] = [];
          }
          analysisMap[mappedDate].push(a);
        });

        const merged = prices.map((p) => {
          const dayAnalyses = analysisMap[p.date];
          if (dayAnalyses && dayAnalyses.length > 0) {
            return {
              date: p.date,
              close: p.close,
              analyses: dayAnalyses,
              decision: dayAnalyses[0].decision,
              confidence: dayAnalyses[0].confidence,
              analysisId: dayAnalyses[0].id,
            };
          }
          return {
            date: p.date,
            close: p.close,
            analyses: null,
            decision: null,
            confidence: null,
            analysisId: null,
          };
        });

        setChartData(merged);
      } else {
        setError(response.data.error || "Failed to load price chart data");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to load price chart data"
      );
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  useEffect(() => {
    if (ticker) {
      fetchChartData();
    }
  }, [ticker, fetchChartData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 320 }}>
        <CircularProgress color="primary" size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 120 }}>
        <Typography variant="body2" color="text.secondary">
          No price data available for the analysis date range.
        </Typography>
      </Box>
    );
  }

  const closes = chartData.map((d) => d.close).filter(Boolean);
  const minPrice = Math.floor(Math.min(...closes) * 0.98);
  const maxPrice = Math.ceil(Math.max(...closes) * 1.02);

  const analysisDots = chartData.filter((d) => d.analyses && d.analyses.length > 0);

  const formatXAxis = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const tickCount = Math.min(chartData.length, 8);
  const step = Math.max(1, Math.floor(chartData.length / tickCount));
  const ticks = chartData
    .filter((_, i) => i % step === 0)
    .map((d) => d.date);

  return (
    <Box>
      <Typography variant="h6" sx={{ color: "#00bfff", fontSize: "1rem", mb: 1 }}>
        Analysis History
      </Typography>

      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 20, bottom: 5, left: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(255,255,255,0.06)"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            ticks={ticks}
            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            tickLine={false}
          />
          <YAxis
            domain={[minPrice, maxPrice]}
            tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            tickLine={false}
            tickFormatter={(v) => `$${v}`}
            width={65}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="close"
            stroke="#00ff00"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: "#00ff00", stroke: "#1a1a1a", strokeWidth: 2 }}
          />
          {analysisDots.map((dot) => (
            <ReferenceDot
              key={dot.date}
              x={dot.date}
              y={dot.close}
              shape={(props) => renderAnalysisDot({ ...props, payload: dot })}
              isFront
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default StockPriceChart;
